import { accountLogin, accountSignup, forgotPassword, resetPassword, fetchMe, resendVerify, logout, myGamesList, createGameApi, deleteGameApi, enterGameSession, claimInviteApi, updateProfileApi, setMyDisplayNameApi, changePasswordApi, type Account, type GameSummary } from "../api";
import { AVATARS, compressImage } from "./avatars";

const LIFE360_IOS = "https://apps.apple.com/app/life360/id384830320";
const LIFE360_ANDROID = "https://play.google.com/store/apps/details?id=com.life360.android.safetymapd";

// Account auth screens (email + password): log in / create account / forgot
// password, plus the reset-password form (from an emailed ?reset= link) and a
// minimal dashboard shell. Games are added in Phase B.

type Mode = "login" | "signup" | "forgot";

const CALC_WARNING = "Calculator mode runs with no login.\n\nYour eliminations and notes are saved only on this device — they are NOT backed up and are NOT shared with a team or game. Clearing your browser will erase them.\n\nOpen the calculator anyway?";
function openCalc() { if (confirm(CALC_WARNING)) { const u = new URL(location.href); u.searchParams.set("calc", "1"); location.href = u.toString(); } }

export function mountAuth(root: HTMLElement, onDone: () => void) {
  let mode: Mode = "login";
  function render() {
    const title = mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Jet Lag NYC";
    root.innerHTML = `
      <div class="login-wrap">
        <form class="login-card" id="f">
          <h1>${title}</h1>
          ${sessionStorage.getItem("jl_invite") ? `<div class="banner">You're invited to a game — log in or create an account to join.</div>` : ""}
          <p class="sub">${mode === "login" ? "Manhattan team edition — log in to your games" : mode === "signup" ? "One account for every game you play or run" : "We'll email you a reset link"}</p>
          ${mode === "signup" ? `<label>Name<input id="name" autocomplete="name" required /></label>` : ""}
          <label>Email<input id="email" type="email" autocomplete="email" autocapitalize="none" required /></label>
          ${mode !== "forgot" ? `<label>Password<div class="pw-wrap"><input id="pw" type="password" autocomplete="${mode === "signup" ? "new-password" : "current-password"}" required /><button type="button" class="pw-toggle" id="pwtoggle" aria-label="Show password">Show</button></div></label>${mode === "signup" ? `<div class="auth-hint">At least 6 characters.</div>` : ""}` : ""}
          <button class="primary" type="submit">${mode === "signup" ? "Create account" : mode === "forgot" ? "Send reset link" : "Log in"}</button>
          <div class="login-error" id="err"></div>
          <div class="auth-links">
            ${mode === "login" ? `<button type="button" data-go="signup" class="link-btn">Create account</button><button type="button" data-go="forgot" class="link-btn">Forgot password?</button>`
              : `<button type="button" data-go="login" class="link-btn">← Back to log in</button>`}
          </div>
          ${mode === "login" ? `<div class="auth-classic"><button type="button" id="calc" class="calc-link">🧮 Open calculator mode <span>distance &amp; elimination tools — no login</span></button></div>` : ""}
        </form>
      </div>`;
    root.querySelector("#calc")?.addEventListener("click", openCalc);
    const pwToggle = root.querySelector<HTMLButtonElement>("#pwtoggle");
    pwToggle?.addEventListener("click", () => { const i = root.querySelector<HTMLInputElement>("#pw")!; const show = i.type === "password"; i.type = show ? "text" : "password"; pwToggle.textContent = show ? "Hide" : "Show"; });
    const form = root.querySelector<HTMLFormElement>("#f")!;
    const err = root.querySelector<HTMLElement>("#err")!;
    root.querySelectorAll<HTMLButtonElement>("[data-go]").forEach((b) => b.addEventListener("click", () => { mode = b.dataset.go as Mode; render(); }));
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      err.textContent = "";
      const btn = form.querySelector("button.primary") as HTMLButtonElement;
      const label = btn.textContent;
      btn.disabled = true; btn.textContent = "…";
      const email = (root.querySelector<HTMLInputElement>("#email")!).value.trim();
      const pw = (root.querySelector<HTMLInputElement>("#pw") || { value: "" } as HTMLInputElement).value;
      try {
        if (mode === "login") { await accountLogin(email, pw); onDone(); }
        else if (mode === "signup") { await accountSignup(email, (root.querySelector<HTMLInputElement>("#name")!).value.trim(), pw); onDone(); }
        else { await forgotPassword(email); err.style.color = "var(--green, #00933C)"; err.textContent = "If that email has an account, a reset link is on its way."; btn.disabled = false; btn.textContent = label!; }
      } catch (e) {
        err.style.color = ""; err.textContent = (e as Error).message;
        btn.disabled = false; btn.textContent = label!;
      }
    });
  }
  render();
}

export function mountResetPassword(root: HTMLElement, token: string, onDone: () => void) {
  root.innerHTML = `
    <div class="login-wrap">
      <form class="login-card" id="f">
        <h1>Set a new password</h1>
        <label>New password<input id="pw" type="password" autocomplete="new-password" required /></label>
        <label>Confirm password<input id="pw2" type="password" autocomplete="new-password" required /></label>
        <button class="primary" type="submit">Save password</button>
        <div class="login-error" id="err"></div>
      </form>
    </div>`;
  const form = root.querySelector<HTMLFormElement>("#f")!;
  const err = root.querySelector<HTMLElement>("#err")!;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    err.textContent = "";
    const pw = (root.querySelector<HTMLInputElement>("#pw")!).value;
    const pw2 = (root.querySelector<HTMLInputElement>("#pw2")!).value;
    if (pw !== pw2) { err.textContent = "Passwords don't match."; return; }
    const btn = form.querySelector("button")!;
    btn.disabled = true; btn.textContent = "Saving…";
    try {
      await resetPassword(token, pw);
      err.style.color = "var(--green, #00933C)";
      err.textContent = "✓ Password updated — you can log in now.";
      setTimeout(onDone, 1200);
    } catch (e) {
      err.style.color = ""; err.textContent = (e as Error).message;
      btn.disabled = false; btn.textContent = "Save password";
    }
  });
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));

// Shared name + avatar/photo picker (used by the first-login wizard and editor).
function pickerHtml(state: { name: string; photo?: string }): string {
  return `
    <div class="prof-preview" id="prof-prev">${state.photo ? `<img src="${state.photo}" alt=""/>` : esc((state.name[0] || "?").toUpperCase())}</div>
    <label class="prof-label">Your name<input id="pname" value="${esc(state.name)}" placeholder="e.g. MBNYC" autocomplete="name" /></label>
    <div class="prof-label">Pick an avatar or upload a photo</div>
    <div class="avatar-grid">
      ${AVATARS.map((a) => `<button type="button" class="avatar-opt ${state.photo === a.url ? "on" : ""}" data-av="${a.url}"><img src="${a.url}" alt=""/></button>`).join("")}
      <label class="avatar-opt upload"><input type="file" accept="image/*" id="pupload" hidden/>＋</label>
    </div>`;
}
function wirePicker(scope: HTMLElement, state: { name: string; photo?: string }) {
  const prev = scope.querySelector<HTMLElement>("#prof-prev")!;
  const refresh = () => { prev.innerHTML = state.photo ? `<img src="${state.photo}" alt=""/>` : esc(((scope.querySelector<HTMLInputElement>("#pname")?.value || state.name)[0] || "?").toUpperCase()); scope.querySelectorAll(".avatar-opt").forEach((b) => b.classList.toggle("on", (b as HTMLElement).dataset.av === state.photo)); };
  scope.querySelector<HTMLInputElement>("#pname")!.addEventListener("input", refresh);
  scope.querySelectorAll<HTMLButtonElement>("[data-av]").forEach((b) => b.addEventListener("click", () => { state.photo = b.dataset.av!; refresh(); }));
  scope.querySelector<HTMLInputElement>("#pupload")!.addEventListener("change", async (e) => {
    const f = (e.target as HTMLInputElement).files?.[0]; if (!f) return;
    try { state.photo = await compressImage(f); refresh(); } catch { alert("Couldn't read that image."); }
  });
}

export function mountProfileWizard(root: HTMLElement, account: Account, onDone: () => void) {
  const state = { name: account.name || account.email.split("@")[0], photo: account.photo };
  let step = 1;
  function render() {
    root.innerHTML = `
      <div class="login-wrap"><div class="login-card prof-card">
        ${step === 1 ? `
          <h1>Set up your profile</h1>
          <p class="sub">This is how teammates and the gamemaster will see you.</p>
          ${pickerHtml(state)}
          <button class="primary" id="next">Continue</button>
        ` : `
          <h1>One more thing</h1>
          <p class="sub">Jet Lag works best when everyone shares live location. Download <b>Life360</b> and join your group's circle.</p>
          <div class="store-btns">
            <a class="store-btn" href="${LIFE360_IOS}" target="_blank" rel="noopener">App Store</a>
            <a class="store-btn" href="${LIFE360_ANDROID}" target="_blank" rel="noopener">Google Play</a>
          </div>
          <button class="primary" id="finish">Finish</button>
          <button class="link-btn" id="skip">Skip for now</button>
        `}
        <div class="login-error" id="err"></div>
      </div></div>`;
    if (step === 1) {
      wirePicker(root, state);
      root.querySelector("#next")!.addEventListener("click", () => { state.name = (root.querySelector<HTMLInputElement>("#pname")!).value.trim() || state.name; step = 2; render(); });
    } else {
      const finish = async () => {
        const btn = root.querySelector("#finish") as HTMLButtonElement; if (btn) { btn.disabled = true; btn.textContent = "Saving…"; }
        try { await updateProfileApi({ name: state.name, photo: state.photo, profileComplete: true }); onDone(); }
        catch (e) { (root.querySelector<HTMLElement>("#err")!).textContent = (e as Error).message; if (btn) { btn.disabled = false; btn.textContent = "Finish"; } }
      };
      root.querySelector("#finish")!.addEventListener("click", finish);
      root.querySelector("#skip")!.addEventListener("click", finish);
    }
  }
  render();
}

export function mountProfileEdit(root: HTMLElement, account: Account, onDone: () => void) {
  const state = { name: account.name || account.email.split("@")[0], photo: account.photo };
  root.innerHTML = `
    <div class="login-wrap"><div class="login-card prof-card">
      <h1>Edit profile</h1>
      ${pickerHtml(state)}
      <button class="primary" id="save">Save</button>
      <div class="login-error" id="err"></div>
      <details class="prof-pw"><summary>Change password</summary>
        <label>Current password<div class="pw-wrap"><input id="cpw" type="password" autocomplete="current-password" /></div></label>
        <label>New password<div class="pw-wrap"><input id="npw" type="password" autocomplete="new-password" /></div></label>
        <div class="auth-hint">At least 6 characters.</div>
        <button class="primary" id="savepw">Update password</button>
        <div class="login-error" id="pwerr"></div>
      </details>
      <button class="link-btn" id="back" style="margin-top:12px">← Back</button>
    </div></div>`;
  wirePicker(root, state);
  root.querySelector("#back")!.addEventListener("click", onDone);
  root.querySelector("#save")!.addEventListener("click", async () => {
    const btn = root.querySelector("#save") as HTMLButtonElement; btn.disabled = true; btn.textContent = "Saving…";
    state.name = (root.querySelector<HTMLInputElement>("#pname")!).value.trim() || state.name;
    try { await updateProfileApi({ name: state.name, photo: state.photo }); onDone(); }
    catch (e) { (root.querySelector<HTMLElement>("#err")!).textContent = (e as Error).message; btn.disabled = false; btn.textContent = "Save"; }
  });
  root.querySelector("#savepw")!.addEventListener("click", async () => {
    const cur = (root.querySelector<HTMLInputElement>("#cpw")!).value;
    const next = (root.querySelector<HTMLInputElement>("#npw")!).value;
    const pwerr = root.querySelector<HTMLElement>("#pwerr")!;
    const btn = root.querySelector("#savepw") as HTMLButtonElement; btn.disabled = true; btn.textContent = "Updating…";
    try { await changePasswordApi(cur, next); pwerr.style.color = "var(--green,#00933C)"; pwerr.textContent = "✓ Password updated."; (root.querySelector<HTMLInputElement>("#cpw")!).value = ""; (root.querySelector<HTMLInputElement>("#npw")!).value = ""; }
    catch (e) { pwerr.style.color = ""; pwerr.textContent = (e as Error).message; }
    btn.disabled = false; btn.textContent = "Update password";
  });
}
const ROLE_LABEL: Record<string, string> = { gm: "Gamemaster", hider: "Hider", seeker: "Seeker", unassigned: "No team yet" };
const MODE_LABEL: Record<string, string> = { group: "Group seeking", podrotation: "Pod rotation", seekingteams: "Seeking teams" };
// Role badge text, aware that group/pod games have no "teams" to be unassigned from.
function roleLabel(g: GameSummary): string {
  if (g.role === "gm") return "Gamemaster";
  if (g.role === "unassigned") return g.mode === "seekingteams" ? "No team yet" : "Player";
  return ROLE_LABEL[g.role] || g.role;
}

export function mountDashboard(root: HTMLElement) {
  root.innerHTML = `<div class="dash"><div class="muted" style="padding:24px">Loading…</div></div>`;
  let me: Account | null = null;

  // Claim a pending invite (?invite= before login), then load the profile.
  const pendingInvite = sessionStorage.getItem("jl_invite");
  if (pendingInvite) sessionStorage.removeItem("jl_invite");
  (pendingInvite ? claimInviteApi(pendingInvite).catch(() => {}) : Promise.resolve())
    .then(() => fetchMe())
    .then((acct) => {
      me = acct;
      if (!me) return logout();
      if (!me.profileComplete) return mountProfileWizard(root, me, () => mountDashboard(root));
      renderShell();
    })
    .catch(() => renderShell());

  function renderShell() {
    const avatar = me?.photo ? `<span class="dash-avatar" style="background-image:url('${me.photo}')"></span>` : `<span class="dash-avatar">${esc(((me?.name || me?.email || "?")[0] || "?").toUpperCase())}</span>`;
    root.innerHTML = `
      <div class="dash">
        <header class="dash-head">
          <div class="dash-who">${avatar}<div><h1>Your games</h1><div class="muted" style="font-size:12px">${esc(me?.name || me?.email || "")}</div></div></div>
          <div class="dash-head-actions"><button class="link-btn" id="editprof">Edit profile</button><button class="ghost" id="logout">Log out</button></div>
        </header>
        <div id="verify-nudge"></div>
        <section class="dash-games" id="games"><div class="muted">Loading…</div></section>
        <div id="create"></div>
        <button class="dash-calc" id="dash-calc">🧮 Calculator mode <span>distance &amp; elimination tools, no game</span></button>
      </div>`;
    root.querySelector("#logout")!.addEventListener("click", () => logout());
    root.querySelector("#dash-calc")!.addEventListener("click", openCalc);
    root.querySelector("#editprof")!.addEventListener("click", () => { if (me) mountProfileEdit(root, me, () => mountDashboard(root)); });
    if (me && !me.emailVerified) {
      const n = root.querySelector<HTMLElement>("#verify-nudge")!;
      n.innerHTML = `<div class="banner" style="flex-direction:column;align-items:flex-start"><div>Please confirm your email — <b>${esc(me.email)}</b>.</div><button class="link-btn" id="resend">Resend link</button></div>`;
      n.querySelector("#resend")!.addEventListener("click", async (e) => { (e.target as HTMLElement).textContent = "Sent ✓"; await resendVerify(); });
    }
    renderGames();
    renderCreate();
  }

  async function renderGames() {
    const el = root.querySelector<HTMLElement>("#games")!;
    if (!el) return;
    let games: GameSummary[] = [];
    try { games = await myGamesList(); } catch { el.innerHTML = `<div class="muted">Couldn't load your games.</div>`; return; }
    el.innerHTML = games.length
      ? games.map((g) => `<div class="game-row">
          <button class="game-card" data-game="${g.id}">
            <div class="game-card-main"><div class="game-name">${esc(g.name)}</div><div class="game-sub">${MODE_LABEL[g.mode] || "Seeking teams"}${g.memberCount ? ` · ${g.memberCount} player${g.memberCount === 1 ? "" : "s"}` : ""}</div></div>
            <span class="role-badge role-${g.role}">${esc(roleLabel(g))}</span>
          </button>
          ${g.isGm
            ? `<div class="game-shownas"><span>You're the gamemaster</span><button class="link-btn danger" data-delgame="${g.id}">🗑 Delete game</button></div>`
            : `<div class="game-shownas"><span>Your name in this game: <b>${esc(g.displayName || me?.name || me?.email || "you")}</b></span><button class="link-btn" data-rename="${g.id}">Change</button></div>`}
        </div>`).join("")
      : `<div class="muted">No games yet — start one below.</div>`;
    el.querySelectorAll<HTMLButtonElement>("[data-game]").forEach((b) => b.addEventListener("click", async () => {
      b.disabled = true;
      try { await enterGameSession(b.dataset.game!); location.reload(); }
      catch (e) { alert((e as Error).message); b.disabled = false; }
    }));
    el.querySelectorAll<HTMLButtonElement>("[data-rename]").forEach((b) => b.addEventListener("click", async () => {
      const cur = games.find((g) => g.id === b.dataset.rename)?.displayName || me?.name || "";
      const name = prompt("How should others see you in this game?", cur);
      if (name == null) return;
      try { await setMyDisplayNameApi(b.dataset.rename!, name.trim()); await renderGames(); }
      catch (e) { alert((e as Error).message); }
    }));
    el.querySelectorAll<HTMLButtonElement>("[data-delgame]").forEach((b) => b.addEventListener("click", async () => {
      const g = games.find((x) => x.id === b.dataset.delgame);
      if (!g) return;
      if (!confirm(`⚠️ Permanently delete “${g.name}”?\n\nThis removes the game and ALL of its teams, players, invites and board history — for everyone. This CANNOT be undone.`)) return;
      b.disabled = true; b.textContent = "Deleting…";
      try { await deleteGameApi(g.id); await renderGames(); }
      catch (e) { alert((e as Error).message); b.disabled = false; b.textContent = "🗑 Delete game"; }
    }));
  }

  function renderCreate() {
    const el = root.querySelector<HTMLElement>("#create")!;
    el.innerHTML = `<button class="primary" id="new-game">+ Start a new game</button>`;
    el.querySelector("#new-game")!.addEventListener("click", () => {
      el.innerHTML = `
        <form class="create-form" id="cf">
          <input id="gname" placeholder="Game name (e.g. Saturday Hunt)" required />
          <select id="gmode"><option value="seekingteams">Seeking teams</option><option value="group">Group seeking</option><option value="podrotation">Pod Rotation</option></select>
          <div class="create-actions"><button type="button" class="ghost" id="cancel">Cancel</button><button class="primary" type="submit">Create game</button></div>
          <div class="login-error" id="cerr"></div>
        </form>`;
      el.querySelector("#cancel")!.addEventListener("click", renderCreate);
      el.querySelector<HTMLFormElement>("#cf")!.addEventListener("submit", async (e) => {
        e.preventDefault();
        const name = (el.querySelector<HTMLInputElement>("#gname")!).value.trim();
        const mode = (el.querySelector<HTMLSelectElement>("#gmode")!).value;
        const btn = el.querySelector("button[type=submit]") as HTMLButtonElement;
        btn.disabled = true; btn.textContent = "Creating…";
        try { await createGameApi(name, mode); renderCreate(); await renderGames(); }
        catch (err) { (el.querySelector<HTMLElement>("#cerr")!).textContent = (err as Error).message; btn.disabled = false; btn.textContent = "Create game"; }
      });
      el.querySelector<HTMLInputElement>("#gname")!.focus();
    });
  }
}
